import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

interface BoardCardMenuProps {
  /** Shown only for people who may take the item back out of review (server rulebook) */
  onWithdraw: () => void;
}

/**
 * The small "more" menu of a Release Board card (In review column, sender / creator only). One action for now:
 * Withdraw from review. It sits above the card's click / drag handling: pressing it must neither open the drawer nor
 * start a drag. Hidden until the card is hovered or focused on a mouse device, always visible on touch.
 */
export const BoardCardMenu = ({ onWithdraw }: BoardCardMenuProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={t('release_card_menu')}
          data-testid="release-card-menu"
          onClick={(e) => e.stopPropagation()}
          // dnd-kit's sensors listen on mouse / touch start of the card: keep them away from the button
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className={cn(
            'absolute bottom-1.5 right-1.5 z-20 flex h-8 w-8 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] items-center justify-center rounded-full',
            'bg-transparent text-gray-800',
            'transition-opacity duration-150 focus:outline-none [&:hover>span]:border-primary-300 [&:focus-visible>span]:border-primary-600',
            open
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100'
          )}
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-transparent bg-white/90 shadow-sm backdrop-blur-sm">
            <Icon name={ICONS.ELLIPSIS_VERTICAL} size="sm" ariaHidden />
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="end"
          sideOffset={6}
          onClick={(e) => e.stopPropagation()}
          className="z-[60] min-w-[190px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg focus:outline-none"
        >
          <button
            type="button"
            data-testid="release-card-withdraw"
            onClick={() => {
              setOpen(false);
              onWithdraw();
            }}
            className="flex min-h-[44px] w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-100"
          >
            <Icon name={ICONS.UNDO} size="sm" ariaHidden />
            {t('release_withdraw_cta')}
          </button>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
